import { Box, Flex, Text } from '@chakra-ui/react'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { magenta } from '../../../../theme'
import { GavelIcon } from './GavelIcon'

interface IAuctionCountdownProps {
  auctionEndsAt: string | null
  auctionEnded: boolean
  /** Início do evento (`date_start` + `time_start`), usado como origem da barra de progresso. */
  eventStartsAt: string | null
}

/** Abaixo disso o cronômetro passa a contar em segundos (1 min e 40 s). */
const SECONDS_THRESHOLD_MS = 100_000

/** Espessura do anel dos dois círculos, em px. */
const RING_THICKNESS = 5

interface ITimerRingProps {
  progress: number
  /** Reta final: o anel pulsa para dar urgência. */
  isEnding?: boolean
  children: ReactNode
}

/**
 * Círculo com anel cinza e um arco magenta, como no mockup.
 *
 * O anel é uma camada própria atrás do conteúdo: ele é desenhado com um `conic-gradient`
 * recortado por uma máscara radial, e a máscara valeria também para o ícone e o número
 * se eles fossem filhos do mesmo elemento.
 */
function TimerRing({ progress, isEnding = false, children }: ITimerRingProps) {
  const sweep = Math.max(0, Math.min(1, progress)) * 360

  return (
    <Flex
      position="relative"
      align="center"
      justify="center"
      w="4.6vw"
      h="4.6vw"
      minW="58px"
      minH="58px"
      flexShrink={0}
      color="white"
      sx={
        isEnding
          ? {
              animation: 'timerRingPulse 1s ease-in-out infinite',
              '@keyframes timerRingPulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.06)' }
              }
            }
          : {}
      }
    >
      {/*
        O halo fica no PAI da camada mascarada, não nela: a ordem de renderização do CSS é
        filter -> clip -> mask, então um drop-shadow aplicado no próprio anel seria recortado
        pela máscara junto com o resto e nunca apareceria.
      */}
      <Box
        position="absolute"
        inset={0}
        sx={{
          filter: `drop-shadow(0 0 6px rgba(250, 0, 105, ${isEnding ? 0.85 : 0.55}))`,
          transition: 'filter 400ms ease',
          ...(isEnding
            ? {
                animation: 'timerGlowPulse 1s ease-in-out infinite',
                '@keyframes timerGlowPulse': {
                  '0%, 100%': { filter: 'drop-shadow(0 0 5px rgba(250, 0, 105, 0.5))' },
                  '50%': { filter: 'drop-shadow(0 0 16px rgba(250, 0, 105, 0.95))' }
                }
              }
            : {})
        }}
      >
        <Box
          position="absolute"
          inset={0}
          borderRadius="full"
          sx={{
            background: `conic-gradient(from 0deg, ${magenta[500]} 0deg ${sweep}deg, rgba(255,255,255,0.18) ${sweep}deg 360deg)`,
            transition: 'background 900ms linear',
            mask: `radial-gradient(farthest-side, transparent calc(100% - ${RING_THICKNESS}px), #000 calc(100% - ${RING_THICKNESS}px))`,
            WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${RING_THICKNESS}px), #000 calc(100% - ${RING_THICKNESS}px))`
          }}
        />
      </Box>

      {children}
    </Flex>
  )
}

/**
 * "O leilão termina em..." — rodapé do card da direita.
 *
 * O `auction_ends_at` muda no meio do leilão (anti-sniping estende, o admin pode encerrar
 * manualmente), e é o hook da tela que reage a esses eventos do WebSocket — aqui só
 * recontamos a cada segundo a partir do valor recebido.
 */
export function AuctionCountdown({ auctionEndsAt, auctionEnded, eventStartsAt }: IAuctionCountdownProps) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  // Origem da barra: o início do evento. Quando ele não serve (evento que ainda não começou,
  // ou sem horário), a referência passa a ser o maior tempo restante já visto nesta tela —
  // assim o anel preenche desde que o telão foi ligado, e um lance com anti-sniping, que
  // estende o prazo, apenas aumenta a referência em vez de estourar a conta.
  const maxRemainingRef = useRef(0)

  useEffect(() => {
    if (!auctionEndsAt) {
      setRemainingMs(null)
      return undefined
    }

    const endsAt = new Date(auctionEndsAt).getTime()

    if (Number.isNaN(endsAt)) {
      setRemainingMs(null)
      return undefined
    }

    const tick = () => setRemainingMs(endsAt - Date.now())

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [auctionEndsAt])

  const isCounting = !auctionEnded && remainingMs !== null && remainingMs > 0
  const isEnding = isCounting && (remainingMs as number) <= SECONDS_THRESHOLD_MS

  // Nunca em horas: minutos enquanto faltar mais de 1 min e 40 s — inclusive passando de
  // 60, como "90 min." — e segundos abaixo disso.
  let amount = '--'
  let unit = ''

  if (isCounting) {
    const totalSeconds = Math.floor((remainingMs as number) / 1000)

    // Sempre dois dígitos: "01 h" em vez de "1 h", para o número não dançar dentro do anel.
    const pad = (value: number) => String(value).padStart(2, '0')

    if (totalSeconds * 1000 >= SECONDS_THRESHOLD_MS) {
      amount = pad(Math.floor(totalSeconds / 60))
      unit = 'min.'
    } else {
      amount = pad(totalSeconds)
      unit = 'seg.'
    }
  }

  // Progresso do leilão: 0 no início, 1 no encerramento. Prazo vencido conta como cheio
  // mesmo antes de o worker fechar — senão o anel esvazia justo no minuto final, enquanto
  // o rótulo já diz "ENCERRANDO...".
  let progress = auctionEnded || (remainingMs !== null && remainingMs <= 0) ? 1 : 0

  if (isCounting) {
    const endMs = new Date(auctionEndsAt as string).getTime()
    const startMs = eventStartsAt ? new Date(eventStartsAt).getTime() : NaN
    const usesEventStart = !Number.isNaN(startMs) && startMs < endMs && startMs <= Date.now()

    maxRemainingRef.current = Math.max(maxRemainingRef.current, remainingMs as number)

    const span = usesEventStart ? endMs - startMs : maxRemainingRef.current

    if (span > 0) progress = 1 - (remainingMs as number) / span
  }

  let label = 'O LEILÃO TERMINA EM...'
  if (auctionEnded) label = 'LEILÃO ENCERRADO'
  else if (remainingMs === null) label = 'LEILÃO EM ANDAMENTO'
  else if (!isCounting) label = 'ENCERRANDO...'

  return (
    <Flex align="center" gap="1vw" borderTop="1px solid" borderColor="whiteAlpha.200" pt="1.4vh">
      <TimerRing progress={progress} isEnding={isEnding}>
        <GavelIcon size="clamp(20px, 2vw, 42px)" />
      </TimerRing>

      <Text
        flex="1"
        textAlign="center"
        fontSize="clamp(13px, 1.35vw, 30px)"
        fontWeight="600"
        lineHeight="1.15"
        letterSpacing="0.02em"
        color="white"
      >
        {label}
      </Text>

      <TimerRing progress={progress} isEnding={isEnding}>
        <Box textAlign="center" lineHeight="1">
          <Text fontSize="clamp(16px, 1.6vw, 34px)" fontWeight="700" color={isEnding ? 'magenta.500' : 'white'}>
            {amount}
          </Text>
          {unit && (
            <Text fontSize="clamp(9px, 0.72vw, 15px)" color="whiteAlpha.800">
              {unit}
            </Text>
          )}
        </Box>
      </TimerRing>
    </Flex>
  )
}
